import { useCallback, useEffect, useState } from 'react';
import { Loading } from './components/Feedback';
import { get, post, setCsrf } from './lib/api';
import { Dashboard } from './pages/Dashboard';
import { LandingAuth } from './pages/LandingAuth';
import { ProjectShell } from './pages/ProjectShell';
import { CommercialPage } from './pages/CommercialPage';
import { AccountPage } from './pages/AccountPage';
import { AdminPage } from './pages/AdminPage';
import { LegalPage } from './pages/LegalPage';
import type { User } from './types';

function usePath() {
  const [path, setPath] = useState(window.location.pathname);
  useEffect(() => {
    const listener = () => setPath(window.location.pathname);
    window.addEventListener('popstate', listener);
    return () => window.removeEventListener('popstate', listener);
  }, []);
  const navigate = useCallback((next: string) => {
    window.history.pushState({}, '', next);
    setPath(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  return { path, navigate };
}

export default function App() {
  const { path, navigate } = usePath();
  const [user, setUser] = useState<User | null>(null);
  const [checking, setChecking] = useState(true);
  useEffect(() => {
    get<{ user: User | null; csrfToken?: string }>('/api/auth/me')
      .then((result) => {
        setUser(result.user);
        setCsrf(result.csrfToken);
        if (result.user && window.location.pathname === '/') navigate('/app');
      })
      .finally(() => setChecking(false));
  }, [navigate]);

  async function logout() {
    await post('/api/auth/logout');
    setCsrf(undefined);
    setUser(null);
    navigate('/');
  }

  if (checking)
    return (
      <div className="boot-screen">
        <Loading label="Membuka SenoSkripsi…" />
      </div>
    );
  const legalPaths = ['/privacy', '/terms', '/ai-disclosure', '/refund-policy'] as const;
  if (legalPaths.includes(path as (typeof legalPaths)[number]))
    return <LegalPage path={path as (typeof legalPaths)[number]} navigate={navigate} />;
  if (!user)
    return (
      <LandingAuth
        onAuthenticated={(next) => {
          setUser(next);
          navigate('/app');
        }}
      />
    );
  if (path === '/app/plans') return <CommercialPage navigate={navigate} />;
  if (path === '/app/account') return <AccountPage navigate={navigate} />;
  if (path === '/app/admin' && user.role === 'admin') return <AdminPage navigate={navigate} />;
  const match = path.match(/^\/app\/project\/([^/]+)\/([^/]+)$/);
  if (match) {
    const allowed = ['overview', 'brain', 'files', 'document', 'revision', 'final'] as const;
    const tab = allowed.includes(match[2] as (typeof allowed)[number])
      ? (match[2] as (typeof allowed)[number])
      : 'overview';
    return <ProjectShell projectId={match[1]} tab={tab} user={user} navigate={navigate} />;
  }
  return <Dashboard user={user} navigate={navigate} onLogout={logout} />;
}
